// components/CopilotDrawer.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import {
  copilotStart,
  copilotListSessions,
  copilotGetSession,
  copilotSendStream,
  copilotRename,
  copilotDestroy,
  copilotBranch,
  uploadAttachment,
  downloadAttachmentBlob,
} from "../services/api";

import MessageBubble from "./copilot/MessageBubble";
import ThinkingBubble from "./copilot/ThinkingBubble";
import Lightbox from "./copilot/Lightbox";

import { useAttachmentCache } from "./copilot/hooks/useAttachmentCache";
import { useDrawerResize } from "./copilot/hooks/useDrawerResize";
import { useThinkingTimers } from "./copilot/hooks/useThinkingTimers";

import { fmtMoney } from "./copilot/utils/format";
import { exportMessageToPpt } from "./copilot/export/pptxExport";

// ✅ PTY026: MAD Pilot model selector (separate from audit models)
const COPILOT_PILOT_MODELS = [
  { id: "gpt-4.1",             label: "GPT-4.1 (default)" },
  { id: "gpt-5",               label: "GPT-5" },
  { id: "gpt-5-mini",          label: "GPT-5 mini" },
  { id: "gpt-4o-mini",         label: "GPT-4o mini" },
  { id: "sonar",               label: "Perplexity Sonar" },
  { id: "sonar-pro",           label: "Perplexity Sonar Pro" },
  { id: "sonar-reasoning",     label: "Perplexity Sonar Reasoning" },
  { id: "gemini-2.5-pro",      label: "Gemini 2.5 Pro" },
  { id: "gemini-2.5-flash",    label: "Gemini 2.5 Flash" },
  { id: "claude-sonnet-4-6",   label: "Claude Sonnet 4.6" },
  { id: "claude-opus-4-6",     label: "Claude Opus 4.6" },
  { id: "claude-haiku-4-5",    label: "Claude Haiku 4.5" },
];

const DRAWER_WIDTH_STORAGE_KEY = "copilot.drawerWidth";
const DEFAULT_DRAWER_WIDTH = 560;
const MIN_DRAWER_WIDTH = 360;
const getMaxDrawerWidth = () => Math.min(980, Math.floor(window.innerWidth * 0.96));
const clampDrawerWidth = (w) => {
  const max = getMaxDrawerWidth();
  return Math.max(MIN_DRAWER_WIDTH, Math.min(max, w));
};

export default function CopilotDrawer({ open, onClose, brandId, auditId, initialSessionId }) {
  const [selectedModel, setSelectedModel] = useState("gpt-4.1");
  const [loading, setLoading] = useState(false);
  const [sessionList, setSessionList] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionPayload, setSessionPayload] = useState(null);

  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState(""); // live token buffer
  const [err, setErr] = useState(null);

  // Fullscreen toggle + restore width
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Selected images before send (local)
  const [pendingImages, setPendingImages] = useState([]); // { file, url, name, size, type }
  const fileInputRef = useRef(null);

  // Lightbox preview
  const [lightbox, setLightbox] = useState({ open: false, url: "", title: "" });

  // Brand confirmation before session start
  const [showBrandConfirm, setShowBrandConfirm] = useState(false);
  const [brandConfirmName, setBrandConfirmName] = useState("");
  const [brandConfirmAliases, setBrandConfirmAliases] = useState("");

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const [optimisticUserMsg, setOptimisticUserMsg] = useState(null);

  const messages = useMemo(() => {
    const arr = sessionPayload?.messages;
    return Array.isArray(arr) ? arr : [];
  }, [sessionPayload]);

  const sessionCostUsd = sessionPayload?.session?.session_cost_usd;
  const isDestroyed = !!sessionPayload?.session?.is_destroyed;

  // ---- attachment cache (downloaded blobs -> object URLs)
  const { getAttachmentObjectUrl, cleanupAllAttachmentUrls } = useAttachmentCache(downloadAttachmentBlob);

  // ---- drawer width / resizing
  const {
    drawerWidth,
    setDrawerWidth,
    isResizing,
    onStartResize,
    prevWidthRef,
  } = useDrawerResize({
    storageKey: DRAWER_WIDTH_STORAGE_KEY,
    defaultWidth: DEFAULT_DRAWER_WIDTH,
    clampWidth: clampDrawerWidth,
    open,
    isFullscreen,
  });

  // ---- thinking timers (send + session load)
  const {
    thinkingVisible,
    sessionLoadingVisible,
    startThinkingTimer,
    startSessionLoadingTimer,
    stopThinking,
    stopSessionLoading,
    resetAllTimers,
  } = useThinkingTimers();

  const syncScrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  // Fetch brand name for the confirmation modal
  const [knownBrandName, setKnownBrandName] = useState("");
  useEffect(() => {
    if (!brandId) return;
    import("../services/api").then(({ fetchBrands }) => {
      fetchBrands().then((brands) => {
        const b = (brands || []).find((x) => Number(x.id) === Number(brandId));
        if (b) setKnownBrandName(b.name || "");
      }).catch(() => {});
    });
  }, [brandId]);

  // Pre-fill brand confirm fields when modal opens — load aliases from DB
  useEffect(() => {
    if (showBrandConfirm) {
      setBrandConfirmName(knownBrandName);
      // Load saved aliases from DB
      import("../services/api").then(({ fetchBrandAliases }) => {
        fetchBrandAliases(brandId).then((data) => {
          const saved = (data?.items || []).map((a) => a.alias).filter(Boolean);
          setBrandConfirmAliases(saved.join(", "));
        }).catch(() => setBrandConfirmAliases(""));
      });
    }
  }, [showBrandConfirm, knownBrandName, brandId]);

  const refreshSessions = async () => {
    const out = await copilotListSessions(brandId);
    setSessionList(out?.items || []);
  };

  useEffect(() => {
    if (!open) return;

    setErr(null);
    (async () => {
      setLoading(true);
      try {
        await refreshSessions();
        // If the caller provided a specific session to focus (e.g. the
        // wizard opening the drawer for the analysis run's linked session),
        // auto-load it instead of leaving the user with a blank chooser.
        if (initialSessionId) {
          try {
            await loadSession(Number(initialSessionId));
          } catch {
            // fall through — user can still pick from the dropdown
          }
        }
      } catch (e) {
        setErr(e?.message || "Failed to load MAD Pilot sessions.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brandId]);

  useEffect(() => {
    if (!open) return;
    syncScrollToBottom();
  }, [open, messages.length]);

  // Scroll as streaming tokens arrive
  useEffect(() => {
    if (!streamingText) return;
    syncScrollToBottom();
  }, [streamingText]);

  // ESC closes copilot pane
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") {
        if (lightbox?.open) {
          e.preventDefault();
          setLightbox({ open: false, url: "", title: "" });
          return;
        }
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, lightbox?.open]);

  // cleanup when closing
  useEffect(() => {
    if (open) return;

    setPendingImages((prev) => {
      prev.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {}
      });
      return [];
    });

    setLightbox({ open: false, url: "", title: "" });
    setIsFullscreen(false);
    setStreamingText("");
    setOptimisticUserMsg(null);
    resetAllTimers();
    cleanupAllAttachmentUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // If sending toggles off, stop any “thinking” indicator
  useEffect(() => {
    if (!sending) stopThinking();
  }, [sending, stopThinking]);

  // If loading toggles off, stop session-loading “thinking”
  useEffect(() => {
    if (!loading) stopSessionLoading();
  }, [loading, stopSessionLoading]);

  const loadSession = async (sessionId) => {
    setErr(null);
    setLoading(true);

    // ✅ show session-loading bubble in the stream
    setSessionPayload(null);
    setActiveSessionId(sessionId);
    startSessionLoadingTimer();

    try {
      const out = await copilotGetSession(sessionId);
      setActiveSessionId(sessionId);
      setSessionPayload(out);
    } catch (e) {
      setErr(e?.message || "Failed to load session.");
    } finally {
      setLoading(false);
      stopSessionLoading();
    }
  };

  const startNewSession = async (autoSend = true, { brandName, brandAliases } = {}) => {
    setErr(null);
    setLoading(true);
    setSessionPayload(null);
    setActiveSessionId(null);
    startSessionLoadingTimer();

    let newSessionId = null;
    let draftPrompt = "";

    try {
      // Always create with auto_send:false so the HTTP call returns immediately.
      // If autoSend=true we stream the initial prompt ourselves below — this avoids
      // a blocking non-streaming LLM call that causes the spinner to hang forever.
      const out = await copilotStart({
        brand_id: brandId,
        audit_id: auditId ?? null,
        title: null,
        model: selectedModel,
        auto_send: false,
        initial_prompt: null,
        brand_name: brandName || null,
        brand_aliases: brandAliases || null,
      });

      if (!out?.session_id) {
        setErr("Failed to create session.");
        return;
      }

      newSessionId = out.session_id;
      draftPrompt = (out?.draft_prompt || "").trim();

      setActiveSessionId(newSessionId);
      setSessionPayload({
        session: {
          id: newSessionId,
          brand_id: brandId,
          audit_id: auditId ?? null,
          title: "MAD Pilot Session",
          default_model: selectedModel,
          session_cost_usd: 0,
          is_destroyed: false,
        },
        messages: [],
      });

      await refreshSessions();
    } catch (e) {
      setErr(e?.message || "Failed to start session.");
      return;
    } finally {
      setLoading(false);
      stopSessionLoading();
    }

    // If manual mode, pre-fill composer and let user send themselves
    if (!autoSend) {
      setComposer(draftPrompt);
      return;
    }

    // Auto mode: stream the initial prompt immediately via the normal stream path.
    // This gives live token rendering instead of a blocking HTTP wait.
    if (!draftPrompt || !newSessionId) return;

    setErr(null);
    setSending(true);
    setStreamingText("");
    startThinkingTimer();

    setOptimisticUserMsg({
      id: "optimistic",
      role: "user",
      text: draftPrompt,
      attachments: [],
      created_at: new Date().toISOString(),
    });
    requestAnimationFrame(() => syncScrollToBottom());

    try {
      const reader = await copilotSendStream(newSessionId, {
        text: draftPrompt,
        model: null,
        attachment_ids: [],
      });

      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let firstToken = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);

          if (payload === "[DONE]") {
            const out = await copilotGetSession(newSessionId);
            setSessionPayload(out);
            setStreamingText("");
            setOptimisticUserMsg(null);
            await refreshSessions();
            return;
          }

          if (payload.startsWith('{"error"')) {
            try {
              const parsed = JSON.parse(payload);
              if (parsed.error) {
                setErr(parsed.error);
                setStreamingText("");
                setOptimisticUserMsg(null);
                const out = await copilotGetSession(newSessionId);
                setSessionPayload(out);
                await refreshSessions();
                return;
              }
            } catch {}
          }

          const token = payload.replace(/\\n/g, "\n");
          accumulated += token;

          if (firstToken) {
            firstToken = false;
            stopThinking();
          }

          setStreamingText(accumulated);
        }
      }
    } catch (e) {
      setErr(e?.message || "Auto-send failed.");
      setStreamingText("");
      setOptimisticUserMsg(null);
    } finally {
      setSending(false);
      stopThinking();
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };

  // Select images
  const onPickImages = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const onlyImages = files.filter((f) => String(f.type || "").toLowerCase().startsWith("image/"));
    if (!onlyImages.length) {
      setErr("Please select image files only.");
      return;
    }

    const next = onlyImages.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      type: file.type,
    }));

    setPendingImages((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removePendingImage = (idx) => {
    setPendingImages((prev) => {
      const copy = [...prev];
      const item = copy[idx];
      copy.splice(idx, 1);
      try {
        if (item?.url) URL.revokeObjectURL(item.url);
      } catch {}
      return copy;
    });
  };

  const clearPendingImages = () => {
    setPendingImages((prev) => {
      prev.forEach((p) => {
        try {
          URL.revokeObjectURL(p.url);
        } catch {}
      });
      return [];
    });
  };

  const onSend = async () => {
    const text = (composer || "").trim();
    if (!activeSessionId) return;
    if (!text && pendingImages.length === 0) return;

    setErr(null);
    setSending(true);
    setStreamingText("");
    startThinkingTimer();

    // Show user message immediately before any network round-trip
    setOptimisticUserMsg({
      id: "optimistic",
      role: "user",
      text,
      attachments: pendingImages.map((p, i) => ({
        id: `pending-${i}`,
        filename: p.name,
        content_type: p.type,
        size_bytes: p.size,
        is_image: true,
        url: p.url,
      })),
      created_at: new Date().toISOString(),
    });
    setComposer("");
    requestAnimationFrame(() => syncScrollToBottom());

    try {
      // Upload pending images first
      const attachment_ids = [];
      for (const p of pendingImages) {
        const up = await uploadAttachment(p.file, brandId, activeSessionId);
        const aid = up?.attachment?.id ?? null;
        if (!aid) throw new Error("Upload succeeded but attachment id was not returned.");
        attachment_ids.push(Number(aid));
      }

      clearPendingImages();

      // Start streaming
      const reader = await copilotSendStream(activeSessionId, {
        text,
        model: null,
        attachment_ids,
      });

      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";

      // Stop the "thinking" bubble once first token arrives
      let firstToken = true;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE lines
        const lines = buffer.split("\n");
        buffer = lines.pop(); // last element may be incomplete

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6); // strip "data: "

          if (payload === "[DONE]") {
            // Stream complete — fetch final session state from server
            const out = await copilotGetSession(activeSessionId);
            setSessionPayload(out);
            setStreamingText("");
            setOptimisticUserMsg(null);
            await refreshSessions();
            return;
          }

          // Check for error frame
          if (payload.startsWith('{"error"')) {
            try {
              const parsed = JSON.parse(payload);
              if (parsed.error) {
                setErr(parsed.error);
                setStreamingText("");
                setOptimisticUserMsg(null);
                // Still refresh to show the error message that was persisted
                const out = await copilotGetSession(activeSessionId);
                setSessionPayload(out);
                await refreshSessions();
                return;
              }
            } catch {}
          }

          // Regular token — unescape \\n back to real newlines
          const token = payload.replace(/\\n/g, "\n");
          accumulated += token;

          if (firstToken) {
            firstToken = false;
            stopThinking(); // hide thinking bubble, show live text instead
          }

          setStreamingText(accumulated);
          syncScrollToBottom();
        }
      }
    } catch (e) {
      setErr(e?.message || "Send failed.");
      setStreamingText("");
      setOptimisticUserMsg(null);
    } finally {
      setSending(false);
      stopThinking();
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  };


  const onRename = async () => {
    if (!activeSessionId || isDestroyed) return;
    const currentTitle = sessionPayload?.session?.title || "";
    const next = window.prompt("Rename session:", currentTitle);
    if (next == null) return;
    const trimmed = String(next).trim();
    if (!trimmed) return;

    setErr(null);
    setSending(true);
    try {
      const out = await copilotRename(activeSessionId, { title: trimmed });
      setSessionPayload(out);
      await refreshSessions();
    } catch (e) {
      setErr(e?.message || "Rename failed.");
    } finally {
      setSending(false);
    }
  };

  const onDestroy = async () => {
    if (!activeSessionId || isDestroyed) return;
    const ok = window.confirm("Delete this session?\n\nThe session and its messages will be permanently removed.");
    if (!ok) return;

    setErr(null);
    setSending(true);
    try {
      const out = await copilotDestroy(activeSessionId);
      setSessionPayload(out);
      await refreshSessions();
    } catch (e) {
      setErr(e?.message || "Destroy failed.");
    } finally {
      setSending(false);
    }
  };

  const onBranchFromMessage = async (messageId) => {
    if (!activeSessionId || isDestroyed) return;

    const ok = window.confirm("Branch from this message?\n\nA new session will be created with history up to this point.");
    if (!ok) return;

    setErr(null);
    setSending(true);
    startThinkingTimer();

    // visually move to new session immediately
    setSessionPayload(null);

    try {
      const out = await copilotBranch(activeSessionId, { from_message_id: Number(messageId) });
      const newSid = out?.session?.id;
      if (newSid) setActiveSessionId(newSid);
      setSessionPayload(out);
      await refreshSessions();
    } catch (e) {
      setErr(e?.message || "Branch failed.");
    } finally {
      setSending(false);
      stopThinking();
    }
  };

  const onExportMessagePpt = useCallback(
    async (msg) => {
      try {
        await exportMessageToPpt({
          activeSessionId,
          auditId,
          msg,
          downloadAttachmentBlob,
        });
      } catch (e) {
        setErr(e?.message || "Export to PPT failed.");
      }
    },
    [activeSessionId, auditId]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sending) onSend();
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      prevWidthRef.current = drawerWidth;
      setIsFullscreen(true);
      return;
    }

    setIsFullscreen(false);
    const restored = clampDrawerWidth(prevWidthRef.current || DEFAULT_DRAWER_WIDTH);
    setDrawerWidth(restored);
    try {
      window.localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(restored));
    } catch {}
  };

  useEffect(() => {
    if (isFullscreen) return;
    prevWidthRef.current = drawerWidth;
  }, [drawerWidth, isFullscreen, prevWidthRef]);

  if (!open) return null;

  const costIsHigh = Number(sessionCostUsd) >= 1;

  const showThinkingPlaceholder = sending && thinkingVisible && !streamingText;
  const showSessionLoadingBubble = loading && !sessionPayload && sessionLoadingVisible;

  return (
    <div className="copilot-backdrop" role="dialog" aria-modal="true">
      <style>{`
        @keyframes copilotSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .streaming-cursor {
          display: inline-block;
          margin-left: 2px;
          animation: cursorBlink 0.8s step-start infinite;
          color: #6b7280;
        }
        .msg-bubble--streaming {
          opacity: 0.92;
        }
      `}</style>

      <div
        className="copilot-drawer"
        style={{
          width: isFullscreen ? "100vw" : drawerWidth,
          maxWidth: isFullscreen ? "100vw" : "96vw",
        }}
      >
        <div
          className="copilot-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize copilot drawer"
          onMouseDown={onStartResize}
          onTouchStart={onStartResize}
          style={{
            cursor: isFullscreen ? "default" : undefined,
          }}
        />

        <div className="copilot-header">
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <div className="copilot-header-title">
              MAD Pilot
              {auditId ? <span className="copilot-header-sub"> • Audit</span> : null}
              {isDestroyed ? <span className="copilot-header-sub"> • (deleted)</span> : null}
              {isFullscreen ? <span className="copilot-header-sub"> • Fullscreen</span> : null}
              {isResizing ? <span className="copilot-header-sub"> • Resizing</span> : null}
            </div>

            <div className="copilot-header-cost">
              Session cost:{" "}
              <strong
                style={{ color: costIsHigh ? "#b91c1c" : "inherit" }}
                title={costIsHigh ? "High cost (≥ $1)" : undefined}
              >
                {fmtMoney(sessionCostUsd)}
              </strong>
            </div>
            {sessionPayload?.session?.default_model && (
              <div className="copilot-header-cost">
                Model: <strong>{sessionPayload.session.default_model}</strong>
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="btn-secondary"
              onClick={toggleFullscreen}
              disabled={sending}
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              ⤢
            </button>
            <button className="btn-secondary" onClick={onClose} disabled={sending} title="Close (Esc)">
              ✕
            </button>
          </div>
        </div>

        <div className="copilot-topbar">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select
              className="fld"
              style={{ height: 36 }}
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={loading || sending}
              title="Model for new sessions"
            >
              {COPILOT_PILOT_MODELS.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
            <button
              className="btn-secondary"
              onClick={() => setShowBrandConfirm(true)}
              disabled={loading || sending}
              title="Start a new MAD Pilot session"
            >
              Start session
            </button>
            <button
              className="btn-secondary"
              onClick={onRename}
              disabled={loading || sending || !activeSessionId || isDestroyed}
              title="Rename this session"
            >
              Rename
            </button>
            <button
              className="btn-secondary"
              onClick={onDestroy}
              disabled={loading || sending || !activeSessionId || isDestroyed}
              title="Delete this session permanently"
            >
              Delete session
            </button>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              className="fld"
              style={{ height: 36, minWidth: 260 }}
              value={activeSessionId || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                loadSession(Number(v));
              }}
              disabled={loading || sending}
            >
              <option value="">Select session…</option>
              {sessionList.map((s) => (
                <option key={s.id} value={s.id}>
                  #{s.id} — {s.title || "Untitled"}
                  {s.is_destroyed ? " (destroyed)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        {err ? (
          <div className="copilot-errorbox">
            <strong style={{ marginRight: 6 }}>Error:</strong> {err}
          </div>
        ) : null}

        <div className="copilot-messages" ref={scrollRef}>
          {!activeSessionId && !loading ? (
            <div style={{ color: "#6b7280", lineHeight: 1.4 }}>
              No session selected. Click <strong>Start session</strong> to begin a new analysis, or pick
              an existing session from the sidebar.
              <br />
              <br />
              Think of sessions like “tabs” in a browser: each one is its own history + context.
            </div>
          ) : null}

          {isDestroyed ? (
            <div style={{ color: "#b45309", lineHeight: 1.4, marginBottom: 12 }}>
              This session has been deleted. You can still view it, but you can’t send new messages.
            </div>
          ) : null}

          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              msg={m}
              getAttachmentObjectUrl={getAttachmentObjectUrl}
              onOpenImage={(url, title) => setLightbox({ open: true, url, title })}
              onBranchFromMessage={onBranchFromMessage}
              disableBranch={!activeSessionId || sending || isDestroyed}
              disableCopy={sending}
              onExportPpt={() => onExportMessagePpt(m)}
              disableExport={!activeSessionId || sending}
            />
          ))}

          {/* Optimistic user message — visible instantly on send */}
          {optimisticUserMsg && (
            <MessageBubble
              key="optimistic"
              msg={optimisticUserMsg}
              getAttachmentObjectUrl={getAttachmentObjectUrl}
              onOpenImage={(url, title) => setLightbox({ open: true, url, title })}
              onBranchFromMessage={() => {}}
              disableBranch={true}
              disableCopy={true}
              onExportPpt={() => {}}
              disableExport={true}
            />
          )}

          {/* ✅ session load placeholder */}
          {showSessionLoadingBubble && <ThinkingBubble kind="loading" />}

          {/* ✅ live streaming bubble — correct CSS classes */}
          {streamingText ? (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 10 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <div className="copilot-bubble is-assistant msg-bubble--streaming">
                  <div className="copilot-bubble-meta">
                    <strong style={{ textTransform: "uppercase" }}>assistant</strong>
                    <span> • streaming…</span>
                  </div>
                  <div className="copilot-md" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {streamingText}
                    <span className="streaming-cursor" aria-hidden="true">▍</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* ✅ send/retry placeholder */}
          {showThinkingPlaceholder && <ThinkingBubble kind="thinking" />}
        </div>

        {/* Composer */}
        <div className="copilot-composer">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={onPickImages}
            disabled={!activeSessionId || sending || isDestroyed}
          />

          {pendingImages.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 10,
                padding: 8,
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fafafa",
              }}
            >
              {pendingImages.map((p, idx) => (
                <div
                  key={`${p.name}-${idx}`}
                  style={{
                    width: 92,
                    borderRadius: 10,
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    background: "#fff",
                    position: "relative",
                  }}
                  title={p.name}
                >
                  <img
                    src={p.url}
                    alt={p.name}
                    style={{ width: "100%", height: 72, objectFit: "cover", display: "block" }}
                    onClick={() => setLightbox({ open: true, url: p.url, title: p.name })}
                  />
                  <div style={{ padding: "6px 8px", fontSize: 11, color: "#374151" }}>
                    {p.name.length > 14 ? `${p.name.slice(0, 14)}…` : p.name}
                  </div>
                  <button
                    type="button"
                    onClick={() => removePendingImage(idx)}
                    disabled={sending}
                    title="Remove"
                    style={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      border: "1px solid rgba(0,0,0,.2)",
                      background: "rgba(255,255,255,.95)",
                      cursor: "pointer",
                      fontWeight: 700,
                      lineHeight: "20px",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            ref={textareaRef}
            className="fld"
            rows={3}
            style={{ width: "100%", resize: "none" }}
            placeholder={
              !activeSessionId ? "Start a session first…" : isDestroyed ? "Session deleted…" : "Type a message… (Enter to send)"
            }
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={!activeSessionId || sending || isDestroyed}
          />

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={!activeSessionId || sending || isDestroyed}
                title="Attach images"
              >
                + Images
              </button>
              {pendingImages.length > 0 ? (
                <button className="btn-secondary" onClick={clearPendingImages} disabled={sending} title="Clear images">
                  Clear
                </button>
              ) : null}
            </div>

            <button
              className="btn-primary"
              onClick={onSend}
              disabled={!activeSessionId || sending || isDestroyed || ((composer || "").trim().length === 0 && pendingImages.length === 0)}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>

        <Lightbox
          open={lightbox.open}
          url={lightbox.url}
          title={lightbox.title}
          onClose={() => setLightbox({ open: false, url: "", title: "" })}
        />
      </div>

      {/* Brand confirmation modal */}
      {showBrandConfirm && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowBrandConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, padding: "28px 32px",
              maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", marginBottom: 6 }}>
              Start MAD Pilot Session
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6, marginBottom: 16 }}>
              Confirm the brand or product name this visibility audit is for.
              If the audit is for a specific product (not the parent brand), change the name below
              so the analysis maps correctly.
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>
                Brand / Product Name *
              </label>
              <input
                type="text"
                className="fld"
                style={{ width: "100%" }}
                value={brandConfirmName}
                onChange={(e) => setBrandConfirmName(e.target.value)}
                placeholder="e.g. Zentiva Magneziu"
                autoFocus
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 4 }}>
                Also known as <span style={{ fontWeight: 400, color: "#9ca3af" }}>(comma-separated, optional)</span>
              </label>
              <input
                type="text"
                className="fld"
                style={{ width: "100%" }}
                value={brandConfirmAliases}
                onChange={(e) => setBrandConfirmAliases(e.target.value)}
                placeholder="e.g. Zentiva, Zentiva Mg, zentiva.ro"
              />
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                Add abbreviations, product names, or domains the brand is known by.
                This improves counting accuracy in the analysis.
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: 13 }}
                onClick={() => setShowBrandConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                style={{ padding: "8px 20px", fontSize: 13 }}
                disabled={!brandConfirmName.trim()}
                onClick={async () => {
                  const aliases = brandConfirmAliases
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean);
                  // Save aliases back to DB for future sessions
                  try {
                    const { setBrandAliases } = await import("../services/api");
                    await setBrandAliases(brandId, aliases);
                  } catch (e) {
                    console.warn("Failed to save aliases:", e);
                  }
                  setShowBrandConfirm(false);
                  startNewSession(true, {
                    brandName: brandConfirmName.trim(),
                    brandAliases: aliases.length ? aliases : null,
                  });
                }}
              >
                Start Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}